import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Select, MenuItem, FormControl, InputLabel, Stack, Chip, IconButton, Tooltip, Typography, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SearchBar from '../../components/SearchBar';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import { formatDate, formatPhone } from '../../utils/formatters';
import { getUsers, updateUserStatus, bulkUpdateUserStatus, deleteUser } from '../../api/endpoints/users';

const UserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';
      const res = await getUsers(params);
      setUsers(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, roleFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelected(users.map((u) => u._id));
    else setSelected([]);
  };

  const handleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleBulkAction = (action) => {
    setBulkAction(action);
    setConfirmOpen(true);
  };

  const confirmBulk = async () => {
    try {
      const isActive = bulkAction === 'active';
      await bulkUpdateUserStatus(selected, isActive);
      toast.success(`${selected.length} users ${isActive ? 'activated' : 'deactivated'}`);
      setSelected([]);
      setConfirmOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update users');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await updateUserStatus(userId, !currentStatus);
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update user status');
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteProcessing, setDeleteProcessing] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteProcessing(true);
      await deleteUser(deleteTarget);
      toast.success('User permanently deleted');
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeleteProcessing(false);
    }
  };

  const columns = [
    { id: 'name', label: 'Name', sortable: true, render: (row) => <Typography fontWeight={500}>{row.name}</Typography> },
    { id: 'phone', label: 'Phone', render: (row) => formatPhone(row.phone) },
    { id: 'email', label: 'Email' },
    { id: 'role', label: 'Role', render: (row) => <StatusChip status={row.role === 'astrologer' ? 'astrologer' : 'user'} /> },
    { id: 'isActive', label: 'Status', render: (row) => <StatusChip status={row.isActive ? 'active' : 'inactive'} /> },
    { id: 'createdAt', label: 'Registered', render: (row) => formatDate(row.createdAt) },
    { id: 'actions', label: 'Actions', render: (row) => (
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="View Details"><IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/users/${row._id}`); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title={row.isActive ? 'Deactivate' : 'Activate'}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleUserStatus(row._id, row.isActive); }}>
            {row.isActive ? <BlockIcon fontSize="small" color="error" /> : <CheckCircleIcon fontSize="small" color="success" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Permanently delete this user">
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row._id); }}>
            <DeleteForeverIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    )},
  ];

  return (
    <Box>
      <PageHeader title="Users" subtitle="Manage all registered users" />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <SearchBar value={search} onChange={setSearch} placeholder="Search by name or phone..." />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Role</InputLabel>
          <Select value={roleFilter} label="Role" onChange={(e) => setRoleFilter(e.target.value)}>
            <MenuItem value="all">All Roles</MenuItem>
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="astrologer">Astrologer</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        {selected.length > 0 && (
          <>
            <Chip label={`${selected.length} selected`} color="primary" variant="outlined" onDelete={() => setSelected([])} />
            <Button size="small" variant="contained" color="success" onClick={() => handleBulkAction('active')}>Activate All</Button>
            <Button size="small" variant="contained" color="error" onClick={() => handleBulkAction('inactive')}>Deactivate All</Button>
          </>
        )}
      </Stack>
      <DataTable
        columns={columns}
        rows={users}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        selected={selected}
        onSelectAllClick={handleSelectAll}
        onSelectClick={handleSelect}
        getRowId={(r) => r._id}
        loading={loading}
        emptyMessage="No users found"
      />
      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Bulk Action"
        message={`Are you sure you want to ${bulkAction === 'active' ? 'activate' : 'deactivate'} ${selected.length} users?`}
        onConfirm={confirmBulk}
        onCancel={() => setConfirmOpen(false)}
        severity={bulkAction === 'active' ? 'success' : 'error'}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete User?"
        message="This will permanently delete this user's account and all associated data. This action cannot be undone."
        confirmText="Delete Forever"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        severity="error"
        loading={deleteProcessing}
      />
    </Box>
  );
};

export default UserList;